import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RotateCcw, AlertTriangle, X } from 'lucide-react';
import { customerAPI } from '../../services/api';
import { useCompanyIdentity } from '../../hooks/useCompanyIdentity';
import { retrackMessage } from '../../utils/retrackMessage';

// "Update STB" quick-action tile — self-service version of the retrack button already on
// the admin/agent customer pages, scoped to the caller's own set-top box (resolved
// server-side, see POST /customers/me/retrack-stb). Customer-facing copy is deliberately
// generic ("Update Sent...") instead of GTPL's own raw response text (e.g. "3149476104 :
// Retrack Successfully with Customer # : 17320144") — that's internal jargon a customer
// can't act on. Shown the same way whether GTPL reports success or failure: staff already
// get pinged on a real failure via notifyRetrackFailure (server-side, unaffected by this),
// so the customer just gets a calm confirmation + a fallback contact instead of an alarming
// error for something they can't fix themselves anyway.

// One exception to the "always show success" rule above: a suspended, payment-due account
// has no live package on GTPL to push to the STB (see the server-side check), so retracking
// would just replay whatever's already cancelled and leave the customer thinking they did
// something when nothing actually reached GTPL. That case gets a real blocking popup instead
// of the calm toast, naming the actual reason and pointing straight at Recharge.
// Same popup, `rechargePending` variant: the package has expired (server code
// RECHARGE_PENDING) — nothing to push to the STB until they recharge.
function PaymentDuePopup({ outstandingBalance, rechargePending = false, onClose }) {
  const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 360,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: '22px 20px', position: 'relative',
      }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" style={{
          position: 'absolute', top: 12, right: 12, border: 'none', background: 'transparent',
          cursor: 'pointer', color: '#94a3b8', padding: 4,
        }}>
          <X size={18} />
        </button>
        <div style={{
          width: 46, height: 46, borderRadius: 12, background: '#fef3c7', display: 'flex',
          alignItems: 'center', justifyContent: 'center', marginBottom: 14,
        }}>
          <AlertTriangle size={22} color="#d97706" />
        </div>
        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
          {rechargePending ? 'Recharge Pending' : 'Payment Due'}
        </div>
        <div style={{ fontSize: '.87rem', color: '#475569', lineHeight: 1.5, marginBottom: outstandingBalance ? 4 : 18 }}>
          {rechargePending
            ? 'Your package has expired. Please recharge your STB first, then tap Update STB again.'
            : 'Your payment is due. Please clear your outstanding balance for sending a package update to your STB.'}
        </div>
        {!!outstandingBalance && (
          <div style={{ fontSize: '.87rem', color: '#dc2626', fontWeight: 700, marginBottom: 18 }}>
            Outstanding: ₹{fmt(outstandingBalance)}
          </div>
        )}
        <Link to="/portal/payments" onClick={onClose} className="btn btn-primary" style={{
          width: '100%', justifyContent: 'center', padding: '10px 0',
        }}>
          {rechargePending ? 'Recharge Now' : 'Pay Now'}
        </Link>
      </div>
    </div>
  );
}

export default function RetrackStbQuickAction({ inline = false }) {
  const { phone } = useCompanyIdentity('catv');
  const [retracking, setRetracking] = useState(false);
  const [paymentDue, setPaymentDue] = useState(null); // { outstandingBalance, rechargePending } | null

  const handleClick = async () => {
    if (retracking) return;
    setRetracking(true);
    try {
      await customerAPI.retrackStb();
      toast.success(retrackMessage(phone));
    } catch (err) {
      if (err?.response?.data?.code === 'PAYMENT_DUE') {
        setPaymentDue({ outstandingBalance: err.response.data?.data?.outstandingBalance });
      } else if (err?.response?.data?.code === 'RECHARGE_PENDING') {
        setPaymentDue({ rechargePending: true });
      } else if (err?.response?.data?.code === 'RETRACK_COOLDOWN') {
        // An update went out in the last 15 minutes — say so instead of the "sent" toast.
        toast(err.response.data.message, { icon: '⏳', duration: 6000 });
      } else {
        toast.success(retrackMessage(phone));
      }
    } finally {
      setRetracking(false);
    }
  };

  const popup = paymentDue && (
    <PaymentDuePopup outstandingBalance={paymentDue.outstandingBalance} rechargePending={!!paymentDue.rechargePending} onClose={() => setPaymentDue(null)} />
  );

  if (inline) {
    return (
      <>
        <button onClick={handleClick} disabled={retracking} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', flexShrink: 0,
          border: 'none', borderRadius: 99, background: '#1e3a8a',
          cursor: retracking ? 'not-allowed' : 'pointer', opacity: retracking ? .75 : 1,
          fontSize: '.76rem', fontWeight: 700, color: '#fff',
          boxShadow: '0 1px 5px rgba(30,58,138,.3)',
        }}>
          <RotateCcw size={13} color="#fff" style={{ animation: retracking ? 'spin 1s linear infinite' : 'none', flexShrink: 0 }} />
          {retracking ? 'Updating…' : 'Update STB'}
        </button>
        {popup}
      </>
    );
  }

  return (
    <>
      <button onClick={handleClick} disabled={retracking} style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '18px 10px',
        border: 'none', borderRadius: 14, background: '#1e3a8a',
        cursor: retracking ? 'not-allowed' : 'pointer', opacity: retracking ? .75 : 1, width: '100%',
        boxShadow: '0 2px 8px rgba(30,58,138,.35)',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,.18)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <RotateCcw size={20} color="#fff" style={{ animation: retracking ? 'spin 1s linear infinite' : 'none' }} />
        </div>
        <span style={{ fontSize: '.85rem', fontWeight: 700, color: '#fff', textAlign: 'center' }}>
          {retracking ? 'Updating…' : 'Update STB'}
        </span>
      </button>
      {popup}
    </>
  );
}
